import { motion } from 'framer-motion';
import { ArrowRight, Cloud, Code, Palette, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import Button from './Button';

const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-8">
      <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 via-transparent to-primary-600/10" />

      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500/20 rounded-full blur-3xl opacity-50" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary-600/20 rounded-full blur-3xl opacity-50" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="inline-flex items-center space-x-2 px-4 py-2 glass-effect rounded-full mb-8">
            <Sparkles className="w-4 h-4 text-primary-400" />
            <span className="text-sm font-medium text-gray-300">
              Full-Stack Development & Consulting
            </span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            <span className="block text-white">NiroLabs</span>
            <span className="block text-gradient">Modern Web & Cloud Solutions</span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-400 max-w-4xl mx-auto mb-8">
            Building production-ready applications with modern JavaScript frameworks, AWS serverless
            architecture, and infrastructure as code.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <Link to="/contact">
              <Button size="lg" className="group">
                Start a Project
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/services">
              <Button variant="outline" size="lg">
                View Services
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-effect p-6 rounded-xl"
            >
              <Code className="w-10 h-10 text-primary-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">
                Frontend & Backend
              </h3>
              <p className="text-gray-400 text-sm">
                React, TypeScript, Node.js, .NET — modern full-stack development
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="glass-effect p-6 rounded-xl"
            >
              <Cloud className="w-10 h-10 text-primary-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">
                Cloud & Infrastructure
              </h3>
              <p className="text-gray-400 text-sm">
                AWS serverless, Docker, IaC, PostgreSQL, DynamoDB
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="glass-effect p-6 rounded-xl"
            >
              <Palette className="w-10 h-10 text-primary-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">
                VisualForge Platform
              </h3>
              <p className="text-gray-400 text-sm">
                AI creative tools — early access
              </p>
            </motion.div>
          </div>
        </motion.div>
      </div>

      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2">
        <motion.button
          onClick={() => {
            document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
          }}
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="text-gray-400 hover:text-primary-400 transition-colors cursor-pointer"
          aria-label="Scroll to features section"
        >
          <ArrowRight className="w-6 h-6 rotate-90" />
        </motion.button>
      </div>
    </section>
  );
};

export default HeroSection;